import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import UserPicker from '../../../components/UserPicker';
import tprmService from '../../../services/compliance/tprm.service';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import {
  ScorePreviewResponse,
  ThirdParty,
  Vendor,
  VendorAnswers,
  VendorCreate,
  VendorUpdate,
} from '../../../types/compliance';

// ---------------------------------------------------------------------------
// Form shape
// ---------------------------------------------------------------------------

type SectionKey = 'b' | 'c' | 'd' | 'e' | 'f' | 'g';

interface IntakeFormState {
  // Section A — identity
  third_party_id: string;
  name: string;
  legal_entity_name: string;
  product_service_name: string;
  service_type: string;
  department: string;
  business_owner_oid: string;
  technical_owner_oid: string;
  primary_contact_name: string;
  purpose: string;
  requested_go_live: string;
  is_replacement: boolean;
  replacing_vendor: string;
  annual_value: string; // kept as string in the input
  sub_processors: string;
  // Sections B–G
  answers: VendorAnswers;
  // Free-form
  notes: string;
}

function emptyAnswers(): VendorAnswers {
  return { b: {}, c: {}, d: {}, e: {}, f: {}, g: {} };
}

function emptyForm(): IntakeFormState {
  return {
    third_party_id: '',
    name: '',
    legal_entity_name: '',
    product_service_name: '',
    service_type: '',
    department: '',
    business_owner_oid: '',
    technical_owner_oid: '',
    primary_contact_name: '',
    purpose: '',
    requested_go_live: '',
    is_replacement: false,
    replacing_vendor: '',
    annual_value: '',
    sub_processors: '',
    answers: emptyAnswers(),
    notes: '',
  };
}

// ---------------------------------------------------------------------------
// Shared option lists
// ---------------------------------------------------------------------------

const YES_NO: { value: string; label: string }[] = [
  { value: '', label: '--- Select ---' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

function toBoolOrUndef(v: unknown): boolean | undefined {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
}

function boolToSelect(v: unknown): string {
  const b = toBoolOrUndef(v);
  if (b === true) return 'true';
  if (b === false) return 'false';
  return '';
}

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

interface SectionProps {
  form: IntakeFormState;
  setField: <K extends keyof IntakeFormState>(k: K, v: IntakeFormState[K]) => void;
  setAnswerField: (section: SectionKey, key: string, value: any) => void;
  thirdParties: ThirdParty[];
}

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-label mb-1">
      {children}
      {required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${className || ''}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-body"
    />
  );
}

function CheckboxGroup({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  values: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {options.map((o) => {
          const checked = values.includes(o.value);
          return (
            <label key={o.value} className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  if (e.target.checked) onChange([...values, o.value]);
                  else onChange(values.filter((v) => v !== o.value));
                }}
              />
              {o.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function SectionA({ form, setField, thirdParties }: SectionProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div className="md:col-span-2">
        <Label required>Third Party</Label>
        <select
          value={form.third_party_id}
          onChange={(e) => setField('third_party_id', e.target.value)}
          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-body"
        >
          <option value="">— Select or create a third party —</option>
          {thirdParties.map((tp) => (
            <option key={tp.id} value={tp.id}>
              {tp.legal_entity_name || tp.name}
            </option>
          ))}
        </select>
        <Link
          to="/compliance/third-parties"
          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 mt-1 inline-block"
        >
          + Create new third party
        </Link>
      </div>

      <div>
        <Label required>Vendor / Company Name</Label>
        <TextInput value={form.name} onChange={(v) => setField('name', v)} disabled />
        <p className="text-xs text-gray-500 mt-1">Pulled from selected Third Party.</p>
      </div>
      <div>
        <Label>Legal Entity Name</Label>
        <TextInput
          value={form.legal_entity_name}
          onChange={(v) => setField('legal_entity_name', v)}
          disabled
        />
        <p className="text-xs text-gray-500 mt-1">Pulled from selected Third Party.</p>
      </div>

      <div>
        <Label>Product / Service Name</Label>
        <TextInput
          value={form.product_service_name}
          onChange={(v) => setField('product_service_name', v)}
        />
      </div>
      <div>
        <Label>Service Type</Label>
        <Select
          value={form.service_type}
          onChange={(v) => setField('service_type', v)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: 'saas', label: 'SaaS' },
            { value: 'tms', label: 'TMS' },
            { value: 'wms', label: 'WMS' },
            { value: 'edi_api', label: 'EDI / API' },
            { value: 'carrier_broker', label: 'Carrier / Broker' },
            { value: 'payment', label: 'Payment' },
            { value: 'hardware_iot', label: 'Hardware / IoT' },
            { value: 'other', label: 'Other' },
          ]}
        />
      </div>

      <div>
        <Label>Requesting Department</Label>
        <TextInput value={form.department} onChange={(v) => setField('department', v)} />
      </div>
      <div>
        <Label>Primary Vendor Contact</Label>
        <TextInput
          value={form.primary_contact_name}
          onChange={(v) => setField('primary_contact_name', v)}
        />
      </div>

      <div>
        <Label>Business Owner</Label>
        <UserPicker
          value={form.business_owner_oid}
          onChange={(oid) => setField('business_owner_oid', oid)}
          emailDomain="@gwii.com"
        />
      </div>
      <div>
        <Label>Technical Owner</Label>
        <UserPicker
          value={form.technical_owner_oid}
          onChange={(oid) => setField('technical_owner_oid', oid)}
          emailDomain="@gwii.com"
        />
      </div>

      <div className="md:col-span-2">
        <Label>Purpose / Business Justification</Label>
        <TextArea value={form.purpose} onChange={(v) => setField('purpose', v)} rows={3} />
      </div>

      <div>
        <Label>Requested Go-Live Date</Label>
        <TextInput
          type="date"
          value={form.requested_go_live}
          onChange={(v) => setField('requested_go_live', v)}
        />
      </div>
      <div>
        <Label>Vendor Being Replaced</Label>
        <TextInput
          value={form.replacing_vendor}
          onChange={(v) => setField('replacing_vendor', v)}
        />
        <label className="inline-flex items-center gap-2 text-sm mt-2">
          <input
            type="checkbox"
            checked={form.is_replacement}
            onChange={(e) => setField('is_replacement', e.target.checked)}
          />
          This is a replacement for an existing vendor
        </label>
      </div>

      <div>
        <Label>Annual Value (USD)</Label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500">$</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.annual_value}
            onChange={(e) => setField('annual_value', e.target.value)}
            placeholder="e.g. 125000.00"
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-body"
          />
        </div>
      </div>

      <div className="md:col-span-2">
        <Label>Sub-processors / Fourth-party data handlers</Label>
        <TextArea
          value={form.sub_processors}
          onChange={(v) => setField('sub_processors', v)}
          placeholder="List any vendors your vendor uses to process data..."
          rows={3}
        />
        <p className="text-xs text-gray-500 mt-1">
          GDPR/DPA: list all sub-processors handling personal data.
        </p>
      </div>
    </div>
  );
}

function SectionB({ form, setAnswerField }: SectionProps) {
  const b = form.answers.b;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div>
        <Label>Does the vendor store or process company data?</Label>
        <Select
          value={boolToSelect(b.stores_data)}
          onChange={(v) => setAnswerField('b', 'stores_data', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Volume of data exchanged</Label>
        <Select
          value={(b.data_volume as string) || ''}
          onChange={(v) => setAnswerField('b', 'data_volume', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
        />
      </div>

      <div className="md:col-span-2">
        <CheckboxGroup
          label="What types of data does the vendor handle?"
          values={(b.data_types as string[]) || []}
          onChange={(vals) => setAnswerField('b', 'data_types', vals)}
          options={[
            { value: 'pii', label: 'PII (Personally Identifiable Information)' },
            { value: 'payment_data', label: 'Payment / Card Data' },
            { value: 'customer_data', label: 'Customer Data' },
            { value: 'financial_data', label: 'Financial Data' },
            { value: 'employee_data', label: 'Employee Data' },
            { value: 'driver_telematics', label: 'Driver / Telematics Data' },
            { value: 'shipment_tracking', label: 'Shipment Tracking Data' },
            { value: 'confidential', label: 'Confidential / Proprietary' },
            { value: 'internal', label: 'Internal Use Only' },
            { value: 'public', label: 'Public Data' },
          ]}
        />
      </div>

      <div>
        <Label>Does data leave the United States?</Label>
        <Select
          value={boolToSelect(b.data_leaves_us)}
          onChange={(v) => setAnswerField('b', 'data_leaves_us', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor retain data after contract termination?</Label>
        <Select
          value={boolToSelect(b.retains_after_termination)}
          onChange={(v) => setAnswerField('b', 'retains_after_termination', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>

      <div>
        <Label>Is our data logically segregated from other customers?</Label>
        <Select
          value={boolToSelect(b.data_segregated)}
          onChange={(v) => setAnswerField('b', 'data_segregated', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Is data encrypted at rest?</Label>
        <Select
          value={boolToSelect(b.encryption_at_rest)}
          onChange={(v) => setAnswerField('b', 'encryption_at_rest', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>

      <div>
        <Label>Is data encrypted in transit?</Label>
        <Select
          value={boolToSelect(b.encryption_in_transit)}
          onChange={(v) => setAnswerField('b', 'encryption_in_transit', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

function SectionC({ form, setAnswerField }: SectionProps) {
  const c = form.answers.c;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div>
        <Label>Does vendor integrate with internal systems?</Label>
        <Select
          value={boolToSelect(c.integrates_internal)}
          onChange={(v) => setAnswerField('c', 'integrates_internal', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Integration method</Label>
        <Select
          value={(c.integration_type as string) || ''}
          onChange={(v) => setAnswerField('c', 'integration_type', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: 'none', label: 'None' },
            { value: 'manual', label: 'Manual' },
            { value: 'sftp', label: 'SFTP' },
            { value: 'edi', label: 'EDI' },
            { value: 'api', label: 'API' },
            { value: 'mcp', label: 'MCP' },
            { value: 'direct_db', label: 'Direct DB' },
            { value: 'other', label: 'Other' },
          ]}
        />
      </div>
      <div>
        <Label>Does vendor access production systems?</Label>
        <Select
          value={boolToSelect(c.accesses_prod)}
          onChange={(v) => setAnswerField('c', 'accesses_prod', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor access TMS/WMS?</Label>
        <Select
          value={boolToSelect(c.accesses_tms_wms)}
          onChange={(v) => setAnswerField('c', 'accesses_tms_wms', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor access identity provider (IdP)?</Label>
        <Select
          value={boolToSelect(c.accesses_idp)}
          onChange={(v) => setAnswerField('c', 'accesses_idp', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Do vendor users authenticate to our systems?</Label>
        <Select
          value={boolToSelect(c.vendor_users_authenticate)}
          onChange={(v) => setAnswerField('c', 'vendor_users_authenticate', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>SSO supported?</Label>
        <Select
          value={boolToSelect(c.sso_supported)}
          onChange={(v) => setAnswerField('c', 'sso_supported', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>MFA supported?</Label>
        <Select
          value={boolToSelect(c.mfa_supported)}
          onChange={(v) => setAnswerField('c', 'mfa_supported', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Role-based access controls (RBAC) supported?</Label>
        <Select
          value={boolToSelect(c.rbac_supported)}
          onChange={(v) => setAnswerField('c', 'rbac_supported', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Audit logging available?</Label>
        <Select
          value={boolToSelect(c.audit_logging)}
          onChange={(v) => setAnswerField('c', 'audit_logging', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div className="md:col-span-2">
        <Label>Is privileged access restricted and regularly reviewed?</Label>
        <Select
          value={boolToSelect(c.privileged_access_restricted)}
          onChange={(v) => setAnswerField('c', 'privileged_access_restricted', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

function SectionD({ form, setAnswerField }: SectionProps) {
  const d = form.answers.d;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div>
        <Label>Business impact if vendor becomes unavailable</Label>
        <Select
          value={(d.business_impact as string) || ''}
          onChange={(v) => setAnswerField('d', 'business_impact', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: 'none', label: 'None' },
            { value: 'minor', label: 'Minor' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'critical_stop', label: 'Critical — full stop' },
          ]}
        />
      </div>
      <div>
        <Label>Recovery Time Objective (RTO) for this vendor</Label>
        <Select
          value={(d.rto as string) || ''}
          onChange={(v) => setAnswerField('d', 'rto', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: '<4h', label: '< 4 hours' },
            { value: '<24h', label: '< 24 hours' },
            { value: '1-3d', label: '1-3 days' },
            { value: '>3d', label: '> 3 days' },
          ]}
        />
      </div>
      <div>
        <Label>Supports dispatch operations?</Label>
        <Select
          value={boolToSelect(d.supports_dispatch)}
          onChange={(v) => setAnswerField('d', 'supports_dispatch', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Supports shipment tracking?</Label>
        <Select
          value={boolToSelect(d.supports_shipment_tracking)}
          onChange={(v) => setAnswerField('d', 'supports_shipment_tracking', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Supports financial transactions?</Label>
        <Select
          value={boolToSelect(d.supports_financial_transactions)}
          onChange={(v) => setAnswerField('d', 'supports_financial_transactions', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Supports identity / security functions?</Label>
        <Select
          value={boolToSelect(d.supports_identity_security)}
          onChange={(v) => setAnswerField('d', 'supports_identity_security', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Is there an alternate vendor or fallback plan?</Label>
        <Select
          value={boolToSelect(d.has_alternate)}
          onChange={(v) => setAnswerField('d', 'has_alternate', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Is this vendor a single point of failure?</Label>
        <Select
          value={boolToSelect(d.single_point_of_failure)}
          onChange={(v) => setAnswerField('d', 'single_point_of_failure', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div className="md:col-span-2">
        <Label>Does vendor affect revenue or SLA commitments?</Label>
        <Select
          value={boolToSelect(d.affects_revenue_sla)}
          onChange={(v) => setAnswerField('d', 'affects_revenue_sla', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

function SectionE({ form, setAnswerField }: SectionProps) {
  const e = form.answers.e;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div className="md:col-span-2">
        <CheckboxGroup
          label="Security certifications held by vendor"
          values={(e.certifications as string[]) || []}
          onChange={(vals) => setAnswerField('e', 'certifications', vals)}
          options={[
            { value: 'soc2_type2', label: 'SOC 2 Type II' },
            { value: 'soc2_type1', label: 'SOC 2 Type I' },
            { value: 'iso27001', label: 'ISO 27001' },
            { value: 'pci_dss', label: 'PCI DSS' },
            { value: 'hitrust', label: 'HITRUST' },
            { value: 'gdpr', label: 'GDPR' },
            { value: 'ccpa', label: 'CCPA' },
            { value: 'none', label: 'None' },
          ]}
        />
      </div>
      <div>
        <Label>Does vendor carry cyber liability insurance?</Label>
        <Select
          value={boolToSelect(e.cyber_insurance)}
          onChange={(v) => setAnswerField('e', 'cyber_insurance', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Has vendor had a security breach in the last 3 years?</Label>
        <Select
          value={boolToSelect(e.recent_breach)}
          onChange={(v) => setAnswerField('e', 'recent_breach', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Vulnerability management program in place?</Label>
        <Select
          value={boolToSelect(e.vuln_management)}
          onChange={(v) => setAnswerField('e', 'vuln_management', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Annual penetration testing performed?</Label>
        <Select
          value={boolToSelect(e.pentest_performed)}
          onChange={(v) => setAnswerField('e', 'pentest_performed', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Incident response process documented?</Label>
        <Select
          value={boolToSelect(e.ir_process)}
          onChange={(v) => setAnswerField('e', 'ir_process', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Business continuity / DR plan documented?</Label>
        <Select
          value={boolToSelect(e.bcdr_documented)}
          onChange={(v) => setAnswerField('e', 'bcdr_documented', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div className="md:col-span-2">
        <Label>Dedicated security contact available?</Label>
        <Select
          value={boolToSelect(e.security_contact)}
          onChange={(v) => setAnswerField('e', 'security_contact', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

function SectionF({ form, setAnswerField }: SectionProps) {
  const f = form.answers.f;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div>
        <Label>Contract type</Label>
        <Select
          value={(f.contract_type as string) || ''}
          onChange={(v) => setAnswerField('f', 'contract_type', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: 'msa', label: 'MSA' },
            { value: 'subscription', label: 'Subscription' },
            { value: 'transaction', label: 'Transaction' },
            { value: 'carrier', label: 'Carrier' },
            { value: 'one_time', label: 'One-time' },
          ]}
        />
      </div>
      <div>
        <Label>Estimated annual cost</Label>
        <Select
          value={(f.annual_cost as string) || ''}
          onChange={(v) => setAnswerField('f', 'annual_cost', v || undefined)}
          options={[
            { value: '', label: '--- Select ---' },
            { value: '<10k', label: '< $10K' },
            { value: '10k-100k', label: '$10K - $100K' },
            { value: '100k-500k', label: '$100K - $500K' },
            { value: '>500k', label: '> $500K' },
          ]}
        />
      </div>
      <div>
        <Label>Does contract auto-renew?</Label>
        <Select
          value={boolToSelect(f.auto_renewal)}
          onChange={(v) => setAnswerField('f', 'auto_renewal', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor handle payments on our behalf?</Label>
        <Select
          value={boolToSelect(f.handles_payments)}
          onChange={(v) => setAnswerField('f', 'handles_payments', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor use sub-processors?</Label>
        <Select
          value={boolToSelect(f.has_subprocessors)}
          onChange={(v) => setAnswerField('f', 'has_subprocessors', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>DPA (Data Processing Agreement) in place?</Label>
        <Select
          value={boolToSelect(f.requires_dpa)}
          onChange={(v) => setAnswerField('f', 'requires_dpa', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Contract includes right-to-audit clause?</Label>
        <Select
          value={boolToSelect(f.right_to_audit)}
          onChange={(v) => setAnswerField('f', 'right_to_audit', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>SLA defined in contract?</Label>
        <Select
          value={boolToSelect(f.has_sla)}
          onChange={(v) => setAnswerField('f', 'has_sla', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>High switching cost or lock-in risk?</Label>
        <Select
          value={boolToSelect(f.termination_risk)}
          onChange={(v) => setAnswerField('f', 'termination_risk', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Does vendor represent concentration risk?</Label>
        <Select
          value={boolToSelect(f.concentration_risk)}
          onChange={(v) => setAnswerField('f', 'concentration_risk', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

function SectionG({ form, setAnswerField }: SectionProps) {
  const g = form.answers.g;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      <div>
        <Label>Vendor impacts regulated data (HIPAA, GDPR, CCPA, etc.)?</Label>
        <Select
          value={boolToSelect(g.impacts_regulated_data)}
          onChange={(v) => setAnswerField('g', 'impacts_regulated_data', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Vendor impacts customer contractual obligations?</Label>
        <Select
          value={boolToSelect(g.impacts_customer_obligations)}
          onChange={(v) => setAnswerField('g', 'impacts_customer_obligations', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>SOX-relevant vendor (financial reporting)?</Label>
        <Select
          value={boolToSelect(g.sox_relevant)}
          onChange={(v) => setAnswerField('g', 'sox_relevant', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Triggers privacy obligations?</Label>
        <Select
          value={boolToSelect(g.privacy_obligations)}
          onChange={(v) => setAnswerField('g', 'privacy_obligations', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Requires legal review of contract terms?</Label>
        <Select
          value={boolToSelect(g.requires_legal_review)}
          onChange={(v) => setAnswerField('g', 'requires_legal_review', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
      <div>
        <Label>Cross-border data transfer implications?</Label>
        <Select
          value={boolToSelect(g.cross_border_implications)}
          onChange={(v) => setAnswerField('g', 'cross_border_implications', toBoolOrUndef(v))}
          options={YES_NO}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Accordion wrapper
// ---------------------------------------------------------------------------

function Accordion({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-2 bg-panel">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 text-left font-semibold ${
          open ? 'bg-blue-50 dark:bg-blue-900/30' : ''
        }`}
      >
        <span>{title}</span>
        {open ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

const SECTION_ORDER: { key: 'A' | SectionKey; title: string }[] = [
  { key: 'A', title: 'A — Vendor Identity' },
  { key: 'b', title: 'B — Data Sensitivity' },
  { key: 'c', title: 'C — System / Access Risk' },
  { key: 'd', title: 'D — Operational Criticality' },
  { key: 'e', title: 'E — Security Maturity' },
  { key: 'f', title: 'F — Financial / Contractual' },
  { key: 'g', title: 'G — Compliance / Regulatory' },
];

const DOMAIN_LABELS: Record<string, string> = {
  data_sensitivity: 'Data Sensitivity',
  system_access: 'System / Access',
  operational: 'Operational',
  security: 'Security',
  financial: 'Financial',
  compliance: 'Compliance',
  business_context: 'Business Context',
};

const POSTURE_BADGE: Record<string, string> = {
  Strong: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  Moderate: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'Elevated Risk': 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  'Critical Attention': 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

export default function VendorIntakeForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState<IntakeFormState>(emptyForm());
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([]);
  const [openSection, setOpenSection] = useState<'A' | SectionKey | null>('A');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<ScorePreviewResponse | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);

  const setField = useCallback(
    <K extends keyof IntakeFormState>(k: K, v: IntakeFormState[K]) => {
      setForm((f) => ({ ...f, [k]: v }));
    },
    [],
  );

  const setAnswerField = useCallback(
    (section: SectionKey, key: string, value: any) => {
      setForm((f) => {
        const nextSection = { ...f.answers[section], [key]: value };
        if (value === undefined || value === '' || value === null) {
          delete nextSection[key];
        }
        return {
          ...f,
          answers: { ...f.answers, [section]: nextSection },
        };
      });
    },
    [],
  );

  // --- Load third parties + existing vendor on edit -------------------------
  useEffect(() => {
    thirdPartiesService
      .list()
      .then((tps) => setThirdParties(tps))
      .catch(() => {
        // Non-fatal; form still works with a free-entry fallback in the future.
      });
  }, []);

  // Name + legal entity name are derived from the selected Third Party.
  useEffect(() => {
    if (!form.third_party_id) {
      setForm((f) =>
        f.name || f.legal_entity_name
          ? { ...f, name: '', legal_entity_name: '' }
          : f,
      );
      return;
    }
    const tp = thirdParties.find((t) => t.id === form.third_party_id);
    if (!tp) return;
    const nextName = tp.name;
    const nextLegal = tp.legal_entity_name || '';
    setForm((f) =>
      f.name === nextName && f.legal_entity_name === nextLegal
        ? f
        : { ...f, name: nextName, legal_entity_name: nextLegal },
    );
  }, [form.third_party_id, thirdParties]);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    tprmService
      .getVendor(id)
      .then((v: Vendor) => {
        setForm({
          third_party_id: v.third_party_id,
          name: v.name,
          legal_entity_name: v.legal_entity_name || '',
          product_service_name: v.product_service_name || '',
          service_type: v.service_type || '',
          department: v.department || '',
          business_owner_oid: v.business_owner_oid || '',
          technical_owner_oid: v.technical_owner_oid || '',
          primary_contact_name: v.primary_contact_name || '',
          purpose: v.purpose || '',
          requested_go_live: v.requested_go_live || '',
          is_replacement: !!v.is_replacement,
          replacing_vendor: v.replacing_vendor || '',
          annual_value: v.annual_value || '',
          sub_processors: v.sub_processors || '',
          answers: {
            b: v.answers_b || {},
            c: v.answers_c || {},
            d: v.answers_d || {},
            e: v.answers_e || {},
            f: v.answers_f || {},
            g: v.answers_g || {},
          },
          notes: v.notes || '',
        });
      })
      .catch((error: any) => {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Failed to load vendor', message: msg });
      })
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // --- Debounced score preview ---------------------------------------------
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await tprmService.previewScore({
          answers_b: form.answers.b,
          answers_c: form.answers.c,
          answers_d: form.answers.d,
          answers_e: form.answers.e,
          answers_f: form.answers.f,
          answers_g: form.answers.g,
          service_type: form.service_type,
          is_replacement: form.is_replacement,
        });
        if (!cancelled) setPreview(res);
      } catch {
        // Preview is advisory — ignore errors silently; the form still saves.
      }
    }, 500);
    return () => {
      cancelled = true;
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [
    form.answers,
    form.service_type,
    form.is_replacement,
  ]);

  // --- Save ----------------------------------------------------------------
  const buildPayload = useCallback((): VendorCreate => {
    return {
      third_party_id: form.third_party_id,
      name: form.name,
      legal_entity_name: form.legal_entity_name,
      product_service_name: form.product_service_name,
      service_type: form.service_type,
      department: form.department,
      business_owner_oid: form.business_owner_oid || null,
      technical_owner_oid: form.technical_owner_oid || null,
      primary_contact_name: form.primary_contact_name,
      purpose: form.purpose || null,
      requested_go_live: form.requested_go_live || null,
      is_replacement: form.is_replacement,
      replacing_vendor: form.replacing_vendor,
      annual_value: form.annual_value || null,
      sub_processors: form.sub_processors || null,
      answers: form.answers,
      notes: form.notes || null,
    };
  }, [form]);

  const handleSave = useCallback(
    async (thenSubmit: boolean = false) => {
      // Basic client-side validation — match backend required fields.
      if (!form.third_party_id) {
        setErrorNotification({
          title: 'Missing required field',
          message: 'Third Party is required.',
        });
        setOpenSection('A');
        return;
      }
      if (!form.name || !form.name.trim()) {
        setErrorNotification({
          title: 'Missing required field',
          message: 'Vendor / Company Name is required.',
        });
        setOpenSection('A');
        return;
      }
      const setter = thenSubmit ? setSubmitting : setSaving;
      setter(true);
      try {
        let vendor: Vendor;
        // Treat createdId as "already created in this session" — prevents a
        // second POST when the user double-clicks Save Draft in the window
        // between the createVendor response and the URL updating.
        const targetId = id || createdId;
        if (targetId) {
          const updatePayload: VendorUpdate = buildPayload();
          // Remove third_party_id from update — not part of VendorUpdate
          delete (updatePayload as any).third_party_id;
          vendor = await tprmService.updateVendor(targetId, updatePayload);
        } else {
          vendor = await tprmService.createVendor(buildPayload());
          setCreatedId(vendor.id);
        }
        if (thenSubmit) {
          await tprmService.submit(vendor.id);
          setSuccessNotification({
            title: 'Submitted for Review',
            message: `Vendor "${vendor.name}" has been submitted.`,
          });
          navigate(`/compliance/tprm/vendors/${vendor.id}`);
        } else {
          setSuccessNotification({
            title: 'Saved',
            message: `Vendor "${vendor.name}" saved.`,
          });
          if (!id && !createdId) {
            navigate(`/compliance/tprm/vendors/${vendor.id}/edit`, { replace: true });
          }
        }
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
              : error?.message || 'Unknown error occurred';
        setErrorNotification({
          title: thenSubmit ? 'Failed to submit' : 'Failed to save',
          message: msg,
        });
      } finally {
        setter(false);
      }
    },
    [buildPayload, createdId, form.name, form.third_party_id, id, navigate],
  );

  const sectionBars = useMemo(() => {
    if (!preview) return null;
    return Object.keys(DOMAIN_LABELS).map((k) => {
      const s = preview.section_scores[k] ?? 0;
      return (
        <div key={k} className="mb-2">
          <div className="flex justify-between text-xs mb-0.5">
            <span>{DOMAIN_LABELS[k]}</span>
            <span>{s.toFixed(1)}</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded">
            <div
              className="h-2 rounded bg-blue-500"
              style={{ width: `${Math.min(100, Math.max(0, s))}%` }}
            />
          </div>
        </div>
      );
    });
  }, [preview]);

  const sectionProps: SectionProps = { form, setField, setAnswerField, thirdParties };

  return (
    <div className="h-full w-full p-6 pb-24">
      {/* Breadcrumb + title */}
      <div className="mb-4">
        <div className="text-xs text-gray-500 mb-1">
          <Link to="/compliance/tprm" className="text-blue-600 hover:underline">
            TPRM
          </Link>
          {' › '}
          <Link to="/compliance/tprm?tab=data" className="text-blue-600 hover:underline">
            Register
          </Link>
        </div>
        <h1 className="text-2xl font-bold">{isEdit ? 'Edit Vendor Intake' : 'New Vendor Intake'}</h1>
      </div>

      {loading && (
        <div className="text-center text-gray-500 py-12">Loading vendor...</div>
      )}

      {!loading && (
        <div className="flex gap-4">
          {/* Form column */}
          <div className="flex-1 min-w-0">
            {SECTION_ORDER.map(({ key, title }) => {
              const open = openSection === key;
              const onToggle = () => setOpenSection(open ? null : key);
              return (
                <Accordion key={key} title={title} open={open} onToggle={onToggle}>
                  {key === 'A' && <SectionA {...sectionProps} />}
                  {key === 'b' && <SectionB {...sectionProps} />}
                  {key === 'c' && <SectionC {...sectionProps} />}
                  {key === 'd' && <SectionD {...sectionProps} />}
                  {key === 'e' && <SectionE {...sectionProps} />}
                  {key === 'f' && <SectionF {...sectionProps} />}
                  {key === 'g' && <SectionG {...sectionProps} />}
                </Accordion>
              );
            })}

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mt-4 bg-panel">
              <Label>Notes / Additional Context</Label>
              <TextArea
                value={form.notes}
                onChange={(v) => setField('notes', v)}
                rows={4}
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving || submitting}
                  onClick={() => handleSave(false)}
                  className="px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-highlight disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  disabled={saving || submitting}
                  onClick={() => handleSave(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
              <Link
                to="/compliance/tprm?tab=data"
                className="text-sm text-blue-600 hover:underline"
              >
                Cancel
              </Link>
            </div>
          </div>

          {/* Score Preview column */}
          <aside className="w-80 shrink-0">
            <div className="sticky top-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-panel">
              <h3 className="text-sm font-semibold mb-2">Score Preview</h3>
              {!preview && (
                <p className="text-xs text-gray-500 mb-1">
                  Score will be calculated when you save the form.
                </p>
              )}
              {preview && (
                <>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-bold">
                      {preview.total_score.toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">/ 100</span>
                    {preview.risk_posture && (
                      <span
                        className={`ml-auto px-2 py-0.5 text-xs font-medium rounded ${POSTURE_BADGE[preview.risk_posture] || 'bg-gray-200 text-gray-800'}`}
                      >
                        {preview.risk_posture}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mb-3">
                    Tier: <strong>{preview.risk_tier}</strong>
                  </div>
                  {sectionBars}
                </>
              )}
              <p className="text-xs text-gray-400 mt-3">
                Score = additive risk (higher = more risk).
              </p>
            </div>
          </aside>
        </div>
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={6000}
          />
        </NotificationContainer>
      )}
      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
